// Rendering of fenced ``` blocks coming back from the model.
//
// Answers frequently use a fenced block to draw a "chain" — a sequence of
// nodes joined by arrows, often with a label written on the connector itself:
//
//     UE/application
//        |
//        | Subscription, URSP, requested S-NSSAI and DNN
//        v
//     5G Core
//
// Rendered as <pre> that becomes one very long line inside a horizontally
// scrollable box, which is close to unreadable. When a block looks like such a
// chain we render it as a proper flow diagram — nodes as numbered cards, the
// text carried on a connector as a label on the arrow between them — and
// everything else falls back to a plain (wrapping) code block.

// Fence info strings we drop instead of showing as the first step.
const LANGUAGE_TOKENS = new Set([
  'text', 'txt', 'plain', 'plaintext', 'none', 'raw', 'output', 'console',
  'mermaid', 'flow', 'flowchart', 'ascii', 'diagram',
  'bash', 'sh', 'shell', 'zsh', 'json', 'yaml', 'yml', 'toml', 'ini',
  'js', 'javascript', 'jsx', 'ts', 'typescript', 'tsx', 'python', 'py',
  'java', 'c', 'cpp', 'csharp', 'go', 'rust', 'sql', 'http', 'xml', 'html',
  'css', 'md', 'markdown', 'diff', 'log', 'csv'
]);

// Models mix ASCII (| v -->) with Unicode arrows and box-drawing characters
// (│ ┃ ↓ ▼) in the same diagram, so every classification below accepts both.
const VERTICAL_BARS = '|｜¦│┃╎╏┆┇┊┋';
const DOWN_ARROWS = '↓⬇⇓▼▽⌄';
const RIGHT_ARROW_SRC = '(?:[→⇒➔➜➝➞➟➡▶]|-{1,2}>|={1,2}>)';
const RIGHT_ARROW = new RegExp(RIGHT_ARROW_SRC);

// A line made of nothing but connector characters: "|", "│", "v", "-->", "...".
const CONNECTOR_ONLY = new RegExp(
  `^(?:[${VERTICAL_BARS}${DOWN_ARROWS}vV]|-{1,2}>?|={1,2}>|[.·]{2,}|${RIGHT_ARROW_SRC})+$`
);
// A connector carrying a label: "| Subscription, URSP and DNN", "--> via UPF".
// "v" is deliberately excluded — it would swallow words beginning with v.
const LABELLED_CONNECTOR = new RegExp(
  `^(?:[${VERTICAL_BARS}${DOWN_ARROWS}]|${RIGHT_ARROW_SRC})\\s+(\\S.*)$`
);
const LEADING_ARROW = new RegExp(`^(?:[${DOWN_ARROWS}${VERTICAL_BARS}]|${RIGHT_ARROW_SRC})\\s*`);
const TRAILING_ARROW = new RegExp(`\\s*(?:[${DOWN_ARROWS}${VERTICAL_BARS}]|${RIGHT_ARROW_SRC})$`);

// A tree branch: "├── Slice policy", "│   └── Regional edge", "+-- Central DN".
// Group 1 is everything left of the branch character, so its length is the
// column the branch sits at — that is what gives the node its depth.
const BRANCH_PREFIX = new RegExp(
  `^([ ]*(?:[${VERTICAL_BARS}][ ]*)*)((?:[├└┣┗╠╚╞╘]|\\+|\\\\|\`|\\|)[-─―—]+)[ ]*`
);

const MAX_STEPS = 14;
const MAX_NODES = 24;
const MAX_STEP_LENGTH = 180;

function unescapeHtml(value) {
  return value
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&');
}

function escapeHtml(value) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

// Split a fence body into its info string ("text", "bash", …) and the code.
function splitLanguage(body) {
  const lines = body.split('\n');
  const first = (lines[0] || '').trim();
  if (lines.length > 1 && LANGUAGE_TOKENS.has(first.toLowerCase())) {
    return { language: first.toLowerCase(), code: lines.slice(1).join('\n') };
  }
  return { language: '', code: body };
}

function isConnectorOnly(line) {
  return CONNECTOR_ONLY.test(line.replace(/\s+/g, ''));
}

function usableSteps(steps) {
  return (
    steps.length >= 2 &&
    steps.length <= MAX_STEPS &&
    steps.every(
      step =>
        step.text.length > 0 &&
        step.text.length <= MAX_STEP_LENGTH &&
        (step.label || '').length <= MAX_STEP_LENGTH
    )
  );
}

function stripArrows(line) {
  return line.replace(LEADING_ARROW, '').replace(TRAILING_ARROW, '').trim();
}

// A label is often written across two connector lines ("PDU session request:"
// then "S-NSSAI + DNN"); joining those with a dash reads wrong after a colon.
function joinLabels(labels) {
  return labels.reduce(
    (acc, part) => (!acc ? part : acc + (/[:,(–—-]$/.test(acc) ? ' ' : ' — ') + part),
    ''
  );
}

/**
 * Recognise arrow chains and trees, and return { direction, steps }, or null
 * when the block is ordinary code that should stay in a <pre>.
 *
 * Steps are { text, label, children }. `label` is what was written on the
 * connector leading into that step; `children` are branch nodes hanging off it
 * ("├── …"), which are parts or alternatives, not later steps.
 */
export function parseFlow(code) {
  const rawLines = code.split('\n').map(line => line.replace(/\t/g, '    '));
  const lines = rawLines.filter(line => line.trim());

  if (!lines.length) return null;

  // A chain written on a single line: "A → B → C".
  if (lines.length === 1) {
    const parts = lines[0]
      .trim()
      .split(new RegExp(`\\s*${RIGHT_ARROW_SRC}\\s*`))
      .map(part => part.trim())
      .filter(Boolean);
    const steps = parts.map(text => ({ text, label: '', children: [] }));
    return usableSteps(steps) ? { direction: 'horizontal', steps } : null;
  }

  // Multi-line: classify every line as a node, a connector, a labelled
  // connector or a tree branch, then nest the nodes by the column they sit at.
  const roots = [];
  const stack = [];
  let pendingLabels = [];
  let sawConnector = false;
  let sawBranch = false;
  let total = 0;
  let tooLong = false;

  for (const raw of lines) {
    const line = raw.trim();

    if (isConnectorOnly(line)) {
      sawConnector = true;
      continue;
    }

    // Branches are checked before labelled connectors: a nested branch line
    // starts with the parent's vertical rule ("│   └── x") and would otherwise
    // look like a connector carrying the label "└── x".
    const branch = raw.match(BRANCH_PREFIX);
    let depth;
    let text;

    if (branch) {
      sawBranch = true;
      // One column deeper than a plain node in the same column, so that
      // "├── a" directly under an unindented parent still nests inside it.
      depth = branch[1].length + 1;
      text = raw.slice(branch[0].length).trim();
    } else {
      const labelled = line.match(LABELLED_CONNECTOR);
      if (labelled) {
        sawConnector = true;
        pendingLabels.push(labelled[1].trim());
        continue;
      }
      depth = raw.length - raw.trimStart().length;
      text = stripArrows(line);
    }

    if (!text) continue;
    if (text.length > MAX_STEP_LENGTH) tooLong = true;

    while (stack.length && stack[stack.length - 1].depth >= depth) stack.pop();
    const parent = stack.length ? stack[stack.length - 1].node : null;

    // The first node can't have an incoming connector, so anything collected
    // before it is a stray prefix rather than an edge label.
    const label = !parent && !roots.length ? '' : joinLabels(pendingLabels);
    if (label.length > MAX_STEP_LENGTH) tooLong = true;

    const node = { text, label, children: [] };
    (parent ? parent.children : roots).push(node);
    stack.push({ depth, node });
    pendingLabels = [];
    total++;
  }

  // Without a connector or a branch this is just a list of lines — leave it
  // as code.
  if (!sawConnector && !sawBranch) return null;
  if (tooLong || !roots.length || total < 2 || total > MAX_NODES) return null;

  return { direction: 'vertical', steps: roots };
}

// Branch nodes are the parts of, or the alternatives under, the step that
// carries them. They stay unnumbered and arrowless: numbering them in the main
// column claimed a sequence the source never described.
function renderBranch(children) {
  const items = children
    .map(
      child =>
        `<li class="flow-branch-item">` +
        `<span class="flow-branch-text">${escapeHtml(child.text)}</span>` +
        (child.label ? `<span class="flow-branch-note">${escapeHtml(child.label)}</span>` : '') +
        (child.children.length ? renderBranch(child.children) : '') +
        `</li>`
    )
    .join('');

  return `<ul class="flow-branch">${items}</ul>`;
}

function renderStep(step, index) {
  // "Label: detail" reads better with the label picked out.
  const match = step.text.match(/^([^:]{2,40}):\s+(.+)$/);
  const body = match
    ? `<span class="flow-step-label">${escapeHtml(match[1].trim())}</span>` +
      `<span class="flow-step-detail">${escapeHtml(match[2].trim())}</span>`
    : `<span class="flow-step-label">${escapeHtml(step.text)}</span>`;

  return (
    `<li class="flow-step">` +
    `<span class="flow-step-num" aria-hidden="true">${index + 1}</span>` +
    `<span class="flow-step-body">${body}` +
    (step.children.length ? renderBranch(step.children) : '') +
    `</span>` +
    `</li>`
  );
}

function renderLink(label) {
  // role="presentation" keeps the connector out of the list semantics while
  // leaving its label readable.
  return (
    `<li class="flow-link${label ? '' : ' flow-link-bare'}" role="presentation">` +
    `<span class="flow-link-arrow" aria-hidden="true">↓</span>` +
    (label ? `<span class="flow-link-label">${escapeHtml(label)}</span>` : '') +
    `</li>`
  );
}

function renderFlow({ direction, steps }) {
  const items = steps
    .map((step, index) => {
      // Horizontal chains never carry labels; their arrows are drawn in CSS.
      const link = direction === 'vertical' && index > 0 ? renderLink(step.label) : '';
      return link + renderStep(step, index);
    })
    .join('');

  return (
    `<div class="flow-diagram flow-${direction}" role="group" aria-label="Process flow">` +
    `<ol class="flow-steps">${items}</ol>` +
    `</div>`
  );
}

/**
 * Parse one fenced block of plain (unescaped) text into
 * { language, code, flow } — `flow` is null when the block is ordinary code.
 * Used directly by renderers that build their own markup, such as the React
 * panel in the roadmap explorer.
 */
export function parseCodeBlock(body) {
  const { language, code } = splitLanguage(body.replace(/^\n+|\s+$/g, ''));
  return { language, code, flow: parseFlow(code) };
}

/**
 * Render one fenced block to HTML. `body` is the text between the fences and
 * is expected to be HTML-escaped already (both formatters escape up front).
 */
export function renderCodeBlock(body) {
  const { language, code, flow } = parseCodeBlock(unescapeHtml(body));
  if (flow) return renderFlow(flow);

  const languageClass = language || 'text';
  return `<pre><code class="language-${languageClass}">${escapeHtml(code)}</code></pre>`;
}

/**
 * Both formatters run a long chain of regexes after code blocks are converted
 * (newlines collapse to spaces, "+" turns into a bullet, `__x__` turns bold …),
 * which shreds any HTML left inline. So blocks are parked behind a token that
 * no later rule can match, and put back at the very end.
 */
export function createBlockStash() {
  const blocks = [];

  return {
    /** Replace fenced blocks in `content` with inert tokens. */
    stash(content) {
      const take = (match, body) => {
        blocks.push(renderCodeBlock(body));
        return `⟦NSBLOCK${blocks.length - 1}⟧`;
      };
      return content
        .replace(/```([\s\S]*?)```/g, take)
        .replace(/``([\s\S]*?)``/g, take);
    },

    /** Put the rendered blocks back, unwrapping any paragraph around them. */
    restore(content) {
      if (!blocks.length) return content;

      let out = content
        .replace(/<p>\s*(?:<br\s*\/?>)*\s*(⟦NSBLOCK\d+⟧)\s*(?:<br\s*\/?>)*\s*<\/p>/g, '$1')
        .replace(/(?:<br\s*\/?>)+\s*(⟦NSBLOCK\d+⟧)/g, '$1')
        .replace(/(⟦NSBLOCK\d+⟧)\s*(?:<br\s*\/?>)+/g, '$1');

      blocks.forEach((html, index) => {
        out = out.replace(`⟦NSBLOCK${index}⟧`, () => html);
      });
      return out;
    }
  };
}
